import { GridSortDirection, GridSortItem } from '@material-ui/data-grid'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import * as clipboard from 'clipboard-polyfill/text'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { StateMachineStatus } from 'hooks/use-async-fetch'
import { DateOnly } from 'types/date-only'
import { Category } from 'types/tenants/category'
import { CorestreamProduct } from 'types/tenants/corestream-product'
import * as yup from 'yup'
import { MAX_DATE_ONLY_STRING } from './date-only-utils'

export const specialCharacters: RegExp = /[\s~`!@#$%^&*+=\-[\]\\';,/{}|\\":<>?()._]/ //` //ADO confused by open tick
export const upperCase: RegExp = /[A-Z]/
export const lowerCase: RegExp = /[a-z]/
export const digits: RegExp = /[0-9]/

/** @deprecated use convertMegaBytesToBytes instead */
export const convertMbToB = (num: number): number => num / 1024 / 1024
/** @deprecated use convertBytesToMegaBytes instead */
export const convertBtoMb = (num: number): number => num * 1024 * 1024

export const convertMegaBytesToBytes = (num: number): number => num * 1024 * 1024

export const isMaxDate = (date: Date | string = ''): boolean => new Date(date) >= new Date(MAX_DATE_ONLY_STRING)

function ensure<T>(argument: T | undefined | null, message: string = 'This value was promised to be there.'): T {
	if (argument === undefined || argument === null) {
		throw new TypeError(message)
	}

	return argument
}
/**
 *
 * @param startDate Date to check against
 * @returns boolean
 */
export const isStartDateInPast = (startDate: Date | string | null): boolean => {
	const now = new Date().setHours(0, 0, 0, 0)
	if (!startDate) {
		return false
	}
	if (typeof startDate === 'string') {
		return new Date(startDate).getTime() < now
	}

	return startDate.getTime() < now
}

/**
 *
 * @param date Date to set hours as midnight for
 * @returns Date with time at midnight
 */
export function setTimeToMidnight(date: Date | string): Date {
	if (typeof date === 'string') {
		return new Date(date)
	}

	const dateClone = new Date(date.getTime())
	dateClone.setUTCHours(0, 0, 0, 0)

	return dateClone
}

/**
 *
 * @param date Date to set time to end of day
 * @returns Date a day later
 */
export function setTimeToEndOfDay(date: Date | string): Date {
	if (typeof date === 'string') {
		if (date.endsWith('Z')) {
			date = new Date(date)
		} else {
			date = new Date(date + 'Z')
		}
	}
	date.setUTCHours(23, 59, 59)

	return date
}

/**
 * Converts date from UTC to local time
 * @param date Date to convert
 * @returns Date converted from UTC to local time
 */
export function convertUTCDateToLocalDate(date: Date): Date {
	return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000)
}
/**
 * Formats date in format: Month Day Year: August 1 2020
 * Converts the date to local time first
 * @param date - Date to format
 * @param customFormat - a custom date format to use instead. ex. 'MM/DD/YYYY'
 */
export function formatDate(date: string | Date = '', customFormat: string = ''): string {
	const updatedDate = date ? convertUTCDateToLocalDate(new Date(date)) : new Date()

	return dayjs(updatedDate).format(customFormat || 'MMM DD YYYY')
}
/**
 * Formats date in format: Month Day Year: August 1 2020
 * Does not convert to local time
 * @param date - Date to format
 * @param customFormat - a custom date format to use instead. ex. 'MM/DD/YYYY'
 */
export function formatDateWithoutLocalConversion(date: string | Date = '', customFormat: string = ''): string {
	const updatedDate = date ? new Date(date) : new Date()

	return dayjs(updatedDate).format(customFormat || 'MMM DD YYYY')
}

export function formatUtcDateWithoutConversion(date: string | Date, customFormat: string = 'MMM DD YYYY'): string {
	dayjs.extend(utc)

	return dayjs.utc(date).format(customFormat)
}

export function formatDateAndTime(date: string | Date): string {
	const updatedDate = date ? convertUTCDateToLocalDate(new Date(date)) : new Date()
	const day = updatedDate.getDate().toString()
	const month = updatedDate.toLocaleString('en-us', { month: 'short' })
	const year = updatedDate.getFullYear().toString()

	const hours = updatedDate.getHours()
	const minutes = String(updatedDate.getMinutes()).padStart(2, '0')
	const seconds = String(updatedDate.getSeconds()).padStart(2, '0')

	return `${month} ${day} ${year} ${hours}:${minutes}:${seconds}`
}

export function formatDateAndTimeWithoutLocalConversion(date: Date | string): string {
	const updatedDate = date ? new Date(date) : new Date()
	const day = updatedDate.getDate().toString()
	const month = updatedDate.toLocaleString('en-us', { month: 'short' })
	const year = updatedDate.getFullYear().toString()

	const hours = updatedDate.getHours()
	const minutes = String(updatedDate.getMinutes()).padStart(2, '0')
	const seconds = String(updatedDate.getSeconds()).padStart(2, '0')

	return `${month} ${day} ${year} ${hours}:${minutes}:${seconds}`
}

export function formatRateDate(date: string | Date = '', separator: string | undefined = ' '): string {
	const updatedDate = new Date(date)
	const day = updatedDate.getDate().toString()
	const month = updatedDate.toLocaleString('en-us', { month: 'short' })
	const year = updatedDate.getFullYear().toString()

	return `${month}${separator}${day}${separator}${year}`
}

export function formatDateString(date: string | DateOnly): string {
	if (!date) return ''
	const newDate = date.split('T')[0]
	if (!newDate) return date

	const dateParts = newDate.split('-')
	if (dateParts.length !== 3) return date

	return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`
}

export function formatDateOnlyWithSeperator(
	date: string | Date | DateOnly | null | undefined,
	separator: string = '_',
): string {
	if (!date) return ''
	const newDate = date instanceof Date ? date : new Date(date)

	if (newDate.toString() === 'Invalid Date') return ''

	const day = newDate.toLocaleString('en-us', { day: '2-digit' })
	const month = newDate.toLocaleString('en-us', { month: '2-digit' })
	const year = newDate.getFullYear().toString()

	return `${year}${separator}${month}${separator}${day}`
}

export const formatBool = (value: boolean | number): string => (value === true || value === 1 ? 'Yes' : 'No')

/**
 * Gets the current running environment from the window
 */
export function getEnv(): string {
	const hostname = window && window.location && window.location.hostname
	const LOCAL = 'local'
	const DEV = 'dev'
	const INT = 'int'
	const PROD = 'prod'
	const UAT = 'test'

	if (hostname.includes(DEV) || hostname.includes(LOCAL)) return DEV
	if (hostname.includes(INT)) return INT
	if (hostname.includes(UAT)) return UAT

	return PROD
}

/**
 * Updates isSubmitting flag if state is error or success
 * @param status State machine status - loading, idle, error, success states
 */
export function updateIsSubmitting(status: StateMachineStatus | 'pending', setIsSubmitting): void {
	if (status === 'error' || status === 'success') {
		setIsSubmitting(false)
	}
}

export function mapCategoryToId(categories: Category[] | null, categoryName: string): number {
	const selectedCategory = ensure(categories?.find((category) => category.name === categoryName))

	return selectedCategory.categoryId
}

export function mapIdToCategoryName(categories: Category[] | null, categoryId: number): string {
	const selectedCategory = ensure(categories?.find((category) => category.categoryId === categoryId))

	return selectedCategory?.name
}

export const formatCorestreamProducts = (
	corestreamProducts: CorestreamProduct[] | undefined,
): Array<{ id: string; value: string }> =>
	corestreamProducts?.map((csProduct: CorestreamProduct) => ({
		id: csProduct.corestreamProductId,
		value: csProduct.productName,
	})) ?? []

export function formatMoney(number: number, includeCents: boolean = true): string {
	if (number == null) return ''

	return number.toLocaleString('en-US', {
		currency: 'USD',
		style: 'currency',
		minimumFractionDigits: includeCents ? 2 : 0,
		maximumFractionDigits: includeCents ? 2 : 0,
	})
}

// conver getPasswordError to a yup valiation rule
export const passwordValidation = yup
	.string()
	.required('Password is required.')
	.min(6, 'Password must be at least 6 characters.')
	.matches(lowerCase, 'Password must contain at least one lowercase character.')
	.matches(upperCase, 'Password must contain at least one uppercase character.')
	.matches(specialCharacters, 'Password must contain at least one special character.')
	.matches(digits, 'Password must contain at least one number.')

export class LocalStore {
	static get<T>(key: string): T | null {
		const item: string | null = localStorage.getItem(key)
		if (!item) return null

		const parsedItem: T = JSON.parse(item)

		return parsedItem
	}

	static set<T>(key: string, item: T): void {
		localStorage.setItem(key, JSON.stringify(item))
	}

	static remove(key: string): void {
		localStorage.removeItem(key)
	}
}

export function copyToClipboard(value: string): Promise<void> {
	return clipboard.writeText(value)
}

export function normalize(array, keyToNormalizeBy: string) {
	return array.reduce((byId, item) => ({ ...byId, [item[keyToNormalizeBy]]: item }), {})
}

const areaCodeAndNextThree = (value: string): string => `(${value.slice(0, 3)}) ${value.slice(3)}`

export const formatSsn = (value: string): string => {
	const firstFiveSsn = (val: string): string => `${val.slice(0, 3)}-${val.slice(3, 5)}`
	const finalSsn = (val: string): string => `${val.slice(0, 3)}-${val.slice(3, 5)}-${val.slice(5, 9)}`
	const currentValue: string = convertStringToNumber(value)

	if (currentValue.length < 4) {
		return currentValue
	}
	if (currentValue.length < 6) {
		return firstFiveSsn(currentValue)
	}

	return finalSsn(currentValue)
}

// Phone number utils

// only allows 0-9 inputs
export const convertStringToNumber = (value: string): string => {
	if (!value) return ''

	return value.replace(/[^\d]/g, '')
}

/**
 * Normalizes US phone numbers by removing country code prefix if present
 * Handles +1 or 1 prefix (11 digits total)
 * @param value - Phone number string that may include country code
 * @returns Normalized phone number with only 10 digits
 */
export const normalizeUSPhoneNumber = (value: string): string => {
	if (!value) return '' // ← ADD THIS
	const digits = convertStringToNumber(value)

	// Handle +1 or 1 prefix (11 digits total)
	if (digits.length === 11 && digits.startsWith('1')) {
		return digits.slice(1) // Remove leading 1
	}

	return digits
}

/**
 * @param {string} value - current phone input value
 * @returns {string} '(xxx) xxx-', (xxx) xxx-x', '(xxx) xxx-xx', '(xxx) xxx-xxx', '(xxx) xxx-xxxx'
 */
const finalPhoneNumber = (value: string): string => `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`

export const formatPhoneNumber = (value: string): string => {
	// Normalize the input (handles +1, 1, and various formats)
	const currentValue: string = normalizeUSPhoneNumber(value)

	// Handle empty
	if (!currentValue || currentValue.length === 0) {
		return ''
	}

	if (currentValue.length < 4) {
		return currentValue
	}

	if (currentValue.length < 7) {
		return areaCodeAndNextThree(currentValue)
	}

	return finalPhoneNumber(currentValue)
}
// Phone number utils

export const isValidEmail = (value: string): boolean => {
	const yupString = yup.string()

	return yupString.email().isValidSync(value)
}

export const convertStringToNumberOrNull = (value: string): number | null => {
	const number = Number(value)
	if (value === '' || value === null || isNaN(number)) return null

	return number
}

export const addSpaceBetweenCapitalLetters = (value: string): string => value.replace(/([A-Z])/g, ' $1').trim()

export const removeSpacesInAString = (value: string): string => value.replace(/\s/g, '')

export const clearDateIfNoValueIsSelected = (date: string | MaterialUiPickersDate) => {
	if (date === '') return null

	return date
}

export const handleTableSortingModel = (
	field: string | undefined,
	order: GridSortDirection | undefined,
): GridSortItem[] => {
	if (field) {
		return [{ field: field, sort: order }]
	}

	return []
}

export const deepCloneWithoutKeys = (target: Record<string, any>, keysToRemove: string[]): Record<string, any> => {
	if (target === null || typeof target !== 'object') return target

	if (Array.isArray(target)) return target.map((item) => deepCloneWithoutKeys(item, keysToRemove))

	const clonedObj: Record<string, any> = {}
	for (const key in target) {
		if (target.hasOwnProperty(key) && !keysToRemove.includes(key)) {
			clonedObj[key] = deepCloneWithoutKeys(target[key], keysToRemove)
		}
	}

	return clonedObj
}
